'use client';

import { forwardRef, useId, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { MenuGroupContext } from './context/menu-group.context';
import { extractMenuChildren } from './extract-menu-children';
import { menuListStyles } from './styles/menu-list.styles';
import type { MenuGroupPropsInterface } from './types/menu-group.types';

/**
 * @since 10.0.0
 */
export const MenuGroup = forwardRef<HTMLDivElement, MenuGroupPropsInterface>(
  (props: MenuGroupPropsInterface, forwardedRef) => {
    const { 'aria-label': ariaLabel, children, className, css: cssProp, ...rest } = props;
    const id = useId();
    const { heading, rest: items } = extractMenuChildren(children);
    const contextValue = useMemo(() => ({ id }), [id]);

    return (
      <MenuGroupContext.Provider value={contextValue}>
        <div
          ref={forwardedRef}
          data-c11n-component="Menu.Group"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', 'c11n_menu__group', css(cssProp), className)}
          {...rest}
        >
          {heading}
          {items.length > 0 && (
            <ul
              className={cx('c11n', css(menuListStyles))}
              aria-labelledby={heading ? id : undefined}
              aria-label={heading ? undefined : ariaLabel}
            >
              {items}
            </ul>
          )}
        </div>
      </MenuGroupContext.Provider>
    );
  },
);

MenuGroup.displayName = 'Menu.Group';
